import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { clsx } from '../clsx';
import styles from './MenuItem.module.css';

export interface MenuItemProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  label: ReactNode;
  /** Second line, 11.5 px subtle ("Meine Tasks, Queue, Freigaben"). */
  description?: ReactNode | undefined;
  /** Right-aligned text, 11.5 px muted tabular (task count, "kein Zugriff"). */
  badge?: ReactNode | undefined;
  icon?: ReactNode | undefined;
  /** Accent highlight of the current tool / page. */
  active?: boolean | undefined;
  /** Not permitted: subtle text, still visible, not activatable (screens 12: "Nicht freigeschaltet · kein Zugriff"). */
  locked?: boolean | undefined;
  tone?: 'default' | 'danger' | 'muted' | undefined;
  onSelect?: (() => void) | undefined;
}

/** `role="menuitem"` row of the topbar and card menus. */
export function MenuItem({
  label,
  description,
  badge,
  icon,
  active = false,
  locked = false,
  tone = 'default',
  onSelect,
  disabled,
  className,
  onClick,
  ...rest
}: MenuItemProps) {
  const inert = disabled || locked;
  return (
    <button
      type="button"
      role="menuitem"
      tabIndex={-1}
      aria-disabled={inert || undefined}
      className={clsx(
        styles.item,
        active && styles.active,
        locked && styles.locked,
        disabled && styles.disabled,
        tone === 'danger' && styles.danger,
        tone === 'muted' && styles.muted,
        className,
      )}
      onClick={(event) => {
        onClick?.(event);
        if (event.defaultPrevented || inert) return;
        onSelect?.();
      }}
      {...rest}
    >
      {icon ? <span className={styles.icon}>{icon}</span> : null}
      <span className={styles.text}>
        <span className={styles.label}>{label}</span>
        {description ? <span className={styles.description}>{description}</span> : null}
      </span>
      {badge !== undefined && badge !== null ? <span className={styles.badge}>{badge}</span> : null}
    </button>
  );
}
